/**
 * Expenses Export Utility
 * 
 * Generates ZIP with:
 * - Summary PDF (Récapitulatif with notes, N/A, group indicators)
 * - Justificatifs/ folder (receipt files named with date + amount + description)
 * - Relevés/ folder (original bank statement PDFs)
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { apiCall } from '../utils/auth';
import {
    ACCOUNTING_DOCUMENT_CONFIG,
    createInvoiceListPdfBlob,
    createInvoicePdfBlob,
    getInvoiceFilename
} from './accountingDocuments';
import { drawVerticalDottedCellBorder } from './pdfTableStyles';

import { MONTHS_FR } from '../constants/expenseMonths';

/**
 * Sanitize filename (remove special chars)
 */
const sanitize = (str) => str.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_').substring(0, 40);

/**
 * Format amount for display
 */
const fmtAmt = (v) => v ? parseFloat(v).toFixed(2).replace('.', ',') + '€' : '';

/**
 * Format date YYYY-MM-DD → DD/MM/YYYY
 */
const fmtDate = (d) => {
    if (!d) return '';
    const dateOnly = d.substring(0, 10); // "2026-01-01T00:00:00.000Z" → "2026-01-01"
    const parts = dateOnly.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

/**
 * Short date for filenames: YYYY-MM-DD
 */
const shortDate = (d) => d ? d.substring(0, 10) : 'nodate';

/**
 * Generate summary PDF
 */
export function generateSummaryPDF(expenses, year, month, receiptGroups) {
    const doc = new jsPDF('p', 'mm', 'a4');
    const monthName = MONTHS_FR[month - 1];
    const title = `Relevé de compte — Récapitulatif`;
    const subtitle = `${monthName} ${year} — SAS GNEGNEGNE`;

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 20);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(subtitle, 14, 28);

    // Legend
    doc.setFontSize(8);
    doc.setTextColor(120);
    const legendY = 35;
    doc.setFont('helvetica', 'bold');
    doc.text('Legende :', 14, legendY);
    doc.setFont('helvetica', 'normal');
    doc.text('O         = Justificatif joint (voir dossier Justificatifs/)', 14, legendY + 5);
    doc.text('G1, G2... = Justificatif partage entre plusieurs ecritures', 14, legendY + 10);
    doc.text('N/A       = Justificatif non requis (voir memo)', 14, legendY + 15);
    doc.text('OUI       = Ecriture verifiee', 14, legendY + 20);

    // Table
    const rows = expenses.map(exp => {
        const hasReceipt = exp.has_receipt || !!exp.receipt_url;
        const groupNum = receiptGroups?.idToGroup?.[exp.id];
        const isCPA = !!exp.cpa_exempt;

        let status = '';
        if (isCPA) status = 'N/A';
        else if (groupNum) status = `G${groupNum}`;
        else if (hasReceipt) status = 'O';
        else status = '';

        return [
            fmtDate(exp.date_operation),
            exp.description || '',
            exp.debit ? `-${fmtAmt(exp.debit)}` : '',
            exp.credit ? `+${fmtAmt(exp.credit)}` : '',
            status,
            exp.final_checked ? 'OUI' : '-'
        ];
    });

    autoTable(doc, {
        startY: legendY + 27,
        head: [['Date', 'Libelle', 'Debit', 'Credit', 'Justif.', 'Verifie']],
        body: rows,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [248, 248, 245], textColor: [51, 51, 51], fontStyle: 'bold' },
        columnStyles: {
            0: { cellWidth: 23 },
            1: { cellWidth: 77.78 },
            2: { cellWidth: 22, halign: 'right' },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 19, halign: 'center' }
        },
        didParseCell: (data) => {
            // Add bottom padding for memo container (space below description text)
            if (data.column.index === 1 && data.section === 'body') {
                const exp = expenses[data.row.index];
                if (exp?.note) {
                    const lines = Math.ceil(exp.note.length / 45);
                    const memoH = 5 + (lines * 3.5);
                    data.cell.styles.cellPadding = { top: 2, right: 2, bottom: memoH + 3, left: 2 };
                }
            }
        },
        willDrawCell: (data) => {
            // N/A rows get light purple background
            if (data.section === 'body') {
                const exp = expenses[data.row.index];
                if (exp?.cpa_exempt) {
                    data.cell.styles.fillColor = [243, 229, 245];
                }
            }
        },
        didDrawCell: (data) => {
            drawVerticalDottedCellBorder(doc, data);

            // Draw memo container below description
            if (data.column.index === 1 && data.section === 'body') {
                const exp = expenses[data.row.index];
                if (exp?.note) {
                    const x = data.cell.x + 1;
                    const w = data.cell.width - 2;
                    const lines = Math.ceil(exp.note.length / 45);
                    const boxH = 3 + (lines * 3.5);
                    const y = data.cell.y + data.cell.height - boxH - 1;
                    // Container with red border
                    doc.setFillColor(255, 250, 250);
                    doc.setDrawColor(220, 50, 50);
                    doc.setLineWidth(0.3);
                    doc.roundedRect(x, y, w, boxH, 1, 1, 'FD');
                    // Note text — full, no truncation
                    doc.setFontSize(7.5);
                    doc.setTextColor(80);
                    doc.setFont('helvetica', 'italic');
                    doc.text(exp.note, x + 2, y + 3.2, { maxWidth: w - 4 });
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(51);
                    doc.setDrawColor(0);
                }
            }
        }
    });

    // Summary footer
    const finalY = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || 200) + 10;
    doc.setFontSize(9);
    doc.setTextColor(51);
    const totalDebit = expenses.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0);
    const totalCredit = expenses.reduce((s, e) => s + (parseFloat(e.credit) || 0), 0);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Débit : -${totalDebit.toFixed(2).replace('.', ',')}€`, 14, finalY);
    doc.text(`Total Crédit : +${totalCredit.toFixed(2).replace('.', ',')}€`, 90, finalY);
    doc.text(`Solde : ${(totalCredit - totalDebit).toFixed(2).replace('.', ',')}€`, 155, finalY);

    // Row count footnote that includes note text height
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`${expenses.length} ecritures — Genere le ${new Date().toLocaleDateString('fr-FR')}`, 14, finalY + 6);

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(170);
        doc.text(`${i} / ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    }

    return doc.output('blob');
}

/**
 * Fetch a file as blob with auth
 */
async function fetchFileBlob(url) {
    const res = await apiCall(url);
    if (!res) throw new Error('Unauthorized');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
}

/**
 * Get file extension from URL or content type
 */
function getExtension(url, contentType) {
    // Try from URL
    const urlExt = url.split('.').pop()?.split('?')[0]?.toLowerCase();
    if (['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(urlExt)) return urlExt;
    // Fallback to content type
    if (contentType?.includes('pdf')) return 'pdf';
    if (contentType?.includes('jpeg') || contentType?.includes('jpg')) return 'jpg';
    if (contentType?.includes('png')) return 'png';
    return 'bin';
}

const isNewInVersion = (version, invoiceVersion) =>
    Number.isInteger(invoiceVersion) && Number(version) === invoiceVersion;

/**
 * Main export function
 * @param {Object} options
 * @param {Array} options.expenses
 * @param {Array} options.statements
 * @param {number} options.year
 * @param {number} options.month
 * @param {Object} options.receiptGroups
 * @param {Object} options.exportData - matched credit payments with effective draft values
 * @param {Object} options.include - summary, receipts, statements and invoice options
 * @param {function} options.onProgress - progress callback (0-100)
 */
export async function exportExpenses({ expenses, statements, year, month, receiptGroups, exportData, include, onProgress }) {
    const zip = new JSZip();
    const monthName = MONTHS_FR[month - 1];
    const invoiceVersion = Number(exportData?.invoice_version);
    const invoiceScope = ACCOUNTING_DOCUMENT_CONFIG.exportScopes[include.invoiceScope]
        ? include.invoiceScope
        : 'all';
    const additionsOnly = invoiceScope === 'additions';
    const versionSuffix = include.invoices && Number.isInteger(invoiceVersion)
        ? `_V${invoiceVersion}${ACCOUNTING_DOCUMENT_CONFIG.exportScopes[invoiceScope].fileSuffix}`
        : '';
    const rootName = `Depenses_${year}-${String(month).padStart(2, '0')}_GNEGNEGNE${versionSuffix}`;

    const invoiceTypes = include.invoiceTypes || {};
    const selectedInvoices = (exportData?.invoices || []).filter(invoice =>
        include.invoices && invoiceTypes[invoice.source_type] !== false &&
        (!additionsOnly || isNewInVersion(invoice.issued_in_version, invoiceVersion))
    );
    const selectedCancelledInvoices = (exportData?.cancelled_invoices || []).filter(invoice =>
        include.invoices && invoiceTypes[invoice.source_type] !== false &&
        (!additionsOnly || isNewInVersion(invoice.cancelled_in_version, invoiceVersion))
    );
    const hasSelectedInvoiceRecords = selectedInvoices.length + selectedCancelledInvoices.length > 0;
    let progress = 0;
    const totalSteps = (include.summary ? 1 : 0) +
        (include.receipts ? expenses.filter(e => (e.has_receipt || e.receipt_url) && !e.cpa_exempt).length : 0) +
        (include.statements ? statements.length : 0) +
        (include.invoices && include.invoiceList && hasSelectedInvoiceRecords ? 1 : 0) +
        (include.invoices && include.invoiceFiles ? selectedInvoices.length : 0);
    const step = () => {
        progress++;
        onProgress?.(Math.round((progress / Math.max(totalSteps, 1)) * 100));
    };

    // 1. Summary PDF
    if (include.summary) {
        const pdfBlob = generateSummaryPDF(expenses, year, month, receiptGroups);
        zip.file(`Recapitulatif_${monthName}_${year}.pdf`, pdfBlob);
        step();
    }

    // 2. Receipts
    if (include.receipts) {
        const justifFolder = zip.folder('Justificatifs');
        const addedUrls = new Map(); // track grouped receipts to avoid duplicates

        for (const exp of expenses) {
            if (!exp.receipt_url || exp.cpa_exempt) continue;

            const groupNum = receiptGroups?.idToGroup?.[exp.id];

            // Grouped receipt: add once with group name
            if (groupNum && addedUrls.has(exp.receipt_url)) {
                step();
                continue;
            }

            try {
                const blob = await fetchFileBlob(exp.receipt_url);
                const ext = getExtension(exp.receipt_url, blob.type);

                let filename;
                if (groupNum) {
                    // Group: find all members
                    const members = receiptGroups.groups[exp.receipt_url]?.memberIds || [exp.id];
                    const memberExps = expenses.filter(e => members.includes(e.id));
                    const dates = memberExps.map(e => shortDate(e.date_operation)).sort();
                    const totalAmt = memberExps.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0);
                    filename = `G${groupNum}_${dates[0]}_${dates[dates.length - 1]}_-${totalAmt.toFixed(2)}€.${ext}`;
                    addedUrls.set(exp.receipt_url, filename);
                } else {
                    // Individual
                    const amt = exp.debit ? `-${fmtAmt(exp.debit)}` : `+${fmtAmt(exp.credit)}`;
                    const desc = sanitize(exp.description || 'justificatif');
                    filename = `${shortDate(exp.date_operation)}_${amt}_${desc}.${ext}`;
                }

                justifFolder.file(filename, blob);
            } catch (err) {
                console.error(`Failed to fetch receipt for expense ${exp.id}:`, err);
            }
            step();
        }
    }

    // 3. Statements (original bank PDFs)
    if (include.statements) {
        const relevesFolder = zip.folder('Releves');
        const failedStatements = [];
        for (const stmt of statements) {
            try {
                const blob = await fetchFileBlob(`/api/expenses/statement/${stmt.id}/download`);
                const name = stmt.original_filename || `Releve_${stmt.id}.pdf`;
                relevesFolder.file(name, blob);
                console.log(`[Export] Statement ${stmt.id} added: ${name}`);
            } catch (err) {
                console.error(`[Export] Failed to fetch statement ${stmt.id}:`, err);
                failedStatements.push(stmt.original_filename || `Releve_${stmt.id}.pdf`);
            }
            step();
        }

        if (failedStatements.length > 0) {
            throw new Error(`Relevé(s) bancaire(s) introuvable(s) : ${failedStatements.join(', ')}`);
        }
    }

    // 4. Invoices for payments matched to bank credits. Backend data already
    // resolves user-scoped draft values over source values.
    if (include.invoices && hasSelectedInvoiceRecords) {
        const invoiceFolder = zip.folder('Factures_Credits');
        if (include.invoiceList) {
            invoiceFolder.file(
                `Liste_factures_credits_${year}-${String(month).padStart(2, '0')}${versionSuffix}.pdf`,
                createInvoiceListPdfBlob(selectedInvoices, year, month, {
                    cancelledRows: selectedCancelledInvoices,
                    version: invoiceVersion,
                    scope: invoiceScope
                })
            );
            step();
        }
        if (include.invoiceFiles) {
            for (const invoice of selectedInvoices) {
                const typeConfig = ACCOUNTING_DOCUMENT_CONFIG.types[invoice.source_type];
                const typeFolder = invoiceFolder.folder(typeConfig?.folder || 'Autres');
                typeFolder.file(
                    getInvoiceFilename(invoice, { year, month }),
                    createInvoicePdfBlob([invoice], { year, month })
                );
                step();
            }
        }
    }

    // Generate ZIP
    onProgress?.(99);
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${rootName}.zip`);
    onProgress?.(100);
}
